import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Snackbar,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Chip,
  TextField,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { API_BASE_URL } from '../../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import { LEVEL_NAMES } from '../../../constants/userLevels';
import { usePermissions } from '../../../context/PermissionContext';
import Loader from '../../General/Loader';
import BackButton from '../../../components/BackButton';

const AssignPrivileges = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUserObj, setSelectedUserObj] = useState(null);
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const { can } = usePermissions();
  const [userId, setUserId] = useState(null);

  // Search & Filter states
  const [dialogLevelFilter, setDialogLevelFilter] = useState('');
  const [tableLevelFilter, setTableLevelFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Confirmation Modals State
  const [confirmDeassignModalOpen, setConfirmDeassignModalOpen] = useState(false);
  const [targetUserForDeassign, setTargetUserForDeassign] = useState(null);

  const levelNames = LEVEL_NAMES;

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.userId);
        fetchInitialData(decoded.userId);
      } catch (error) {
        console.error('Error decoding token:', error);
        fetchInitialData(null);
      }
    } else {
      fetchInitialData(null);
    }
  }, []);

  const fetchInitialData = async (reqUserId) => {
    setLoading(true);
    try {
      const userEndpoint = reqUserId
        ? `${API_BASE_URL}/users-with-posts/${reqUserId}`
        : `${API_BASE_URL}/users-with-posts`;

      const [usersResponse, postsResponse] = await Promise.all([
        axios.get(userEndpoint),
        axios.get(`${API_BASE_URL}/posts`),
      ]);

      setPosts(postsResponse.data || []);

      const processedUsers = (usersResponse.data || [])
        .filter((user) => {
          const isSuperAdminName =
            user.fullName && typeof user.fullName === 'string'
              ? user.fullName.toLowerCase().includes('super admin')
              : false;
          return user.level !== 1 && user.email !== 'admin@admin.com' && !isSuperAdminName;
        })
        .map((user) => {
          const rawId = user.stateHeadId || user.displayId || user.hoId || user.msbId || user.agentId || user.usercode;
          const hex = user.id || user._id ? String(user.id || user._id).slice(-6).toUpperCase() : '000000';
          let customId = rawId;
          if (!customId || customId.length > 20) {
            if (user.level === 2) customId = `QPAY-${hex}`;
            else if (user.level === 3) customId = `ST-${hex}`;
            else if (user.level === 4) customId = `CTR-${hex}`;
            else if (user.level === 5) customId = `AGT-${hex}`;
            else customId = `USR-${hex}`;
          }

          const fname = user.firstName || (user.fullName ? user.fullName.split(' ')[0] : '');
          const lname = user.lastName || (user.fullName ? user.fullName.split(' ').slice(1).join(' ') : '');

          return {
            id: user.id || user._id,
            stateHeadId: customId,
            displayId: customId,
            firstName: fname || 'N/A',
            lastName: lname || 'N/A',
            fullName: user.fullName || `${fname} ${lname}`.trim(),
            mobile: user.mobile || user.phoneNumber || 'N/A',
            email: user.email || 'N/A',
            level: user.level,
            levelName: levelNames[user.level] || user.levelName || `Level ${user.level}`,
            currentPost: getPostName(user.posts && user.posts[0], postsResponse.data),
            postId: user.posts && user.posts[0] ? user.posts[0] : null,
          };
        });

      setUsers(processedUsers);
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Error fetching data', 'error');
    }
    setLoading(false);
  };

  const getPostName = (postId, postsList) => {
    if (!postId) return 'No designation assigned';
    const post = (postsList || []).find((p) => p._id === postId);
    return post ? post.postName : 'No designation assigned';
  };

  const getDisplayId = (user) => {
    if (!user) return 'N/A';
    if (user.stateHeadId && user.stateHeadId.length < 20) return user.stateHeadId;
    if (user.displayId && user.displayId.length < 20) return user.displayId;
    const hex = user.id ? String(user.id).slice(-6).toUpperCase() : '000000';
    if (user.level === 2) return `QPAY-${hex}`;
    if (user.level === 3) return `ST-${hex}`;
    if (user.level === 4) return `CTR-${hex}`;
    if (user.level === 5) return `AGT-${hex}`;
    return `USR-${hex}`;
  };

  const handleUserSelect = async (event, newValue) => {
    setSelectedUserObj(newValue);
    setSelectedPost('');

    if (newValue && newValue.id) {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/privileges/modules/${newValue.id}`);
        if (data.posts && data.posts.length > 0) {
          setSelectedPost(data.posts[0]._id);
        }
      } catch (error) {
        showSnackbar('Error fetching user privileges', 'error');
      }
    }
  };

  const handlePostChange = (event) => {
    setSelectedPost(event.target.value);
  };

  const handleSubmit = async () => {
    if (!can('users.designations.assign')) return;
    if (!selectedUserObj || !selectedUserObj.id) {
      showSnackbar('Please select a user', 'error');
      return;
    }

    if (!selectedPost) {
      showSnackbar('Please select a designation', 'error');
      return;
    }

    try {
      await axios.put(`${API_BASE_URL}/privileges/${selectedUserObj.id}`, {
        posts: [selectedPost],
      });

      showSnackbar('Designation assigned successfully', 'success');
      handleCloseDialog();
      await fetchInitialData(userId);
    } catch (error) {
      showSnackbar('Error assigning designation', 'error');
    }
  };

  const handleOpenDeassignConfirm = (user) => {
    if (!can('users.designations.deassign')) return;
    setTargetUserForDeassign(user);
    setConfirmDeassignModalOpen(true);
  };

  const handleConfirmDeassign = async () => {
    if (!targetUserForDeassign) return;
    if (!can('users.designations.deassign')) return;
    try {
      await axios.put(`${API_BASE_URL}/privileges/${targetUserForDeassign.id}`, {
        posts: [],
      });
      showSnackbar(
        `Designation de-assigned successfully from ${targetUserForDeassign.firstName} ${targetUserForDeassign.lastName}`,
        'success'
      );
      fetchInitialData(userId);
    } catch (error) {
      showSnackbar('Error de-assigning designation', 'error');
    } finally {
      setConfirmDeassignModalOpen(false);
      setTargetUserForDeassign(null);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (!can('users.designations.assign')) return;
    setOpen(true);
    setDialogLevelFilter('');
    if (user) {
      setSelectedUserObj(user);
      if (user.postId) setSelectedPost(user.postId);
      else setSelectedPost('');
    } else {
      setSelectedUserObj(null);
      setSelectedPost('');
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedUserObj(null);
    setSelectedPost('');
    setDialogLevelFilter('');
  };

  const handleExportCSV = () => {
    if (!can('users.designations.export')) return;
    if (!displayedUsers || displayedUsers.length === 0) {
      showSnackbar('No designation data to export', 'warning');
      return;
    }

    const headers = [
      'SL',
      'ID',
      'First Name',
      'Last Name',
      'Mobile Number',
      'Email',
      'User Type',
      'Assigned Designation',
    ];
    const csvRows = [headers.join(',')];

    displayedUsers.forEach((u, idx) => {
      const sl = idx + 1;
      const id = getDisplayId(u);
      const fname = u.firstName || 'N/A';
      const lname = u.lastName || 'N/A';
      const mobile = u.mobile || 'N/A';
      const email = u.email || 'N/A';
      const type = u.levelName || 'N/A';
      const post = u.currentPost || 'No designation assigned';

      csvRows.push(
        [
          sl,
          `"${id}"`,
          `"${fname}"`,
          `"${lname}"`,
          `"${mobile}"`,
          `"${email}"`,
          `"${type}"`,
          `"${post}"`,
        ].join(',')
      );
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `assigned_designations_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    showSnackbar('CSV exported successfully', 'success');
  };

  const handleExportPDF = () => {
    if (!can('users.designations.print')) return;
    if (!displayedUsers || displayedUsers.length === 0) {
      showSnackbar('No designation data to export', 'warning');
      return;
    }
    try {
      const doc = new jsPDF();
      doc.text('Assigned Designations Report', 14, 15);
      const tableColumn = [
        'SL',
        'ID',
        'First Name',
        'Last Name',
        'Mobile Number',
        'Email',
        'User Type',
        'Assigned Designation',
      ];
      const tableRows = [];

      displayedUsers.forEach((u, idx) => {
        tableRows.push([
          idx + 1,
          getDisplayId(u),
          u.firstName || 'N/A',
          u.lastName || 'N/A',
          u.mobile || 'N/A',
          u.email || 'N/A',
          u.levelName || 'N/A',
          u.currentPost || 'No designation assigned',
        ]);
      });

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [15, 23, 42] },
      });
      doc.save(`assigned_designations_report_${Date.now()}.pdf`);
      showSnackbar('PDF exported successfully', 'success');
    } catch (e) {
      console.error('Error generating PDF:', e);
      handlePrint();
    }
  };

  const handlePrint = () => {
    if (!can('users.designations.print')) return;
    if (!displayedUsers || displayedUsers.length === 0) {
      showSnackbar('No designation data to print', 'warning');
      return;
    }
    const printWindow = window.open('', '_blank');
    const htmlContent = `
      <html>
        <head>
          <title>Assigned Designations Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #1e293b; }
            h2 { text-align: center; margin-bottom: 20px; color: #0f172a; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px 12px; text-align: left; font-size: 12px; }
            th { background-color: #f1f5f9; font-weight: bold; }
            tr:nth-child(even) { background-color: #f8fafc; }
          </style>
        </head>
        <body>
          <h2>Assigned Designations Report</h2>
          <table>
            <thead>
              <tr>
                <th>SL</th>
                <th>ID</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Mobile Number</th>
                <th>Email</th>
                <th>User Type</th>
                <th>Assigned Designation</th>
              </tr>
            </thead>
            <tbody>
              ${displayedUsers
                .map(
                  (u, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${getDisplayId(u)}</td>
                  <td>${u.firstName || 'N/A'}</td>
                  <td>${u.lastName || 'N/A'}</td>
                  <td>${u.mobile || 'N/A'}</td>
                  <td>${u.email || 'N/A'}</td>
                  <td>${u.levelName || 'N/A'}</td>
                  <td>${u.currentPost || 'No designation assigned'}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
          <script>
            window.onload = function() { window.print(); window.close(); };
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const columns = [
    {
      field: 'sl',
      headerName: 'SL',
      width: 70,
      sortable: false,
      renderCell: (params) => {
        const index = displayedUsers.findIndex((r) => r.id === params.row.id);
        return (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
            {index >= 0 ? index + 1 : ''}
          </Typography>
        );
      },
    },
    {
      field: 'stateHeadId',
      headerName: 'ID',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {getDisplayId(params.row)}
        </Typography>
      ),
    },
    { field: 'firstName', headerName: 'First Name', width: 140 },
    { field: 'lastName', headerName: 'Last Name', width: 140 },
    { field: 'mobile', headerName: 'Mobile Number', width: 140 },
    { field: 'email', headerName: 'Email', width: 180 },
    {
      field: 'levelName',
      headerName: 'User Type',
      width: 150,
      renderCell: (params) => {
        const lvl =
          levelNames[params.row.level] ||
          params.row.levelName ||
          `Level ${params.row.level}`;
        return <Chip label={lvl} color="primary" size="small" variant="outlined" sx={{ fontWeight: 700 }} />;
      },
    },
    {
      field: 'currentPost',
      headerName: 'Assigned Designation',
      width: 220,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: params.value === 'No designation assigned' ? '#94a3b8' : '#0f172a' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 320,
      minWidth: 320,
      sortable: false,
      renderCell: (params) => {
        const hasPost = params.row.currentPost && params.row.currentPost !== 'No designation assigned';
        return (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', my: 'auto', flexWrap: 'nowrap', pr: 1 }}>
            {can('users.designations.assign') && (
              <Button
                variant="outlined"
                size="small"
                color="primary"
                startIcon={<EditIcon fontSize="small" />}
                onClick={() => handleOpenDialog(params.row)}
                sx={{ fontWeight: 600, height: 32, px: 2, whiteSpace: 'nowrap', minWidth: 95 }}
              >
                {hasPost ? 'Change' : 'Assign'}
              </Button>
            )}
            {hasPost && can('users.designations.deassign') && (
              <Button
                variant="contained"
                size="small"
                color="error"
                startIcon={<AssignmentReturnIcon fontSize="small" />}
                onClick={() => handleOpenDeassignConfirm(params.row)}
                sx={{ fontWeight: 700, height: 32, px: 2, whiteSpace: 'nowrap', minWidth: 120 }}
              >
                De-assign
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  const displayedUsers = users.filter((u) => {
    if (tableLevelFilter && String(u.level) !== String(tableLevelFilter)) {
      return false;
    }
    if (designationFilter) {
      if (designationFilter === 'unassigned') {
        if (u.currentPost !== 'No designation assigned') return false;
      } else {
        if (u.currentPost !== designationFilter) return false;
      }
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      const idVal = getDisplayId(u).toLowerCase();
      const fname = (u.firstName || '').toLowerCase();
      const lname = (u.lastName || '').toLowerCase();
      const full = (u.fullName || '').toLowerCase();
      const mobile = (u.mobile || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const post = (u.currentPost || '').toLowerCase();
      const level = (u.levelName || '').toLowerCase();

      return (
        fname.includes(term) ||
        lname.includes(term) ||
        full.includes(term) ||
        idVal.includes(term) ||
        mobile.includes(term) ||
        email.includes(term) ||
        post.includes(term) ||
        level.includes(term)
      );
    }
    return true;
  });

  const filteredUsersForDialog = users.filter((u) => {
    if (dialogLevelFilter && String(u.level) !== String(dialogLevelFilter)) {
      return false;
    }
    return true;
  });

  return (
    <Box sx={{ p: 3, pr: 4, mb: 4, width: '100%', boxSizing: 'border-box' }}>
      <BackButton />
      <br /><br />
      <Paper elevation={0} sx={{ p: 3.5, pr: 4, borderRadius: 3, border: '1px solid #e2e8f0', background: '#ffffff', boxSizing: 'border-box' }}>
        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pr: 1 }}>
          <Box>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 800, mb: 0.5 }}>
              Assign Designation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Assign and manage administrative privileges and designations across all user levels
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {can('users.designations.print') && (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  sx={{ fontWeight: 700 }}
                >
                  Print
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleExportPDF}
                  sx={{ fontWeight: 700 }}
                >
                  PDF
                </Button>
              </>
            )}
            {can('users.designations.export') && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportCSV}
                  sx={{ fontWeight: 700 }}
                >
                  Export CSV
                </Button>
              </>
            )}
            {can('users.designations.assign') && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ fontWeight: 700 }}
              >
                Assign Designation
              </Button>
            )}
          </Box>
        </Box>

        {/* Filter Section */}
        <Paper elevation={0} sx={{ p: 2.5, px: 3, mb: 3, mt: 2, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search Name, ID, Phone, Email or Designation..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Designation</InputLabel>
                <Select
                  value={designationFilter}
                  label="Filter by Designation"
                  onChange={(e) => setDesignationFilter(e.target.value)}
                >
                  <MenuItem value="">All Designations</MenuItem>
                  <MenuItem value="unassigned">Unassigned</MenuItem>
                  {posts.map((p) => (
                    <MenuItem key={p._id} value={p.postName}>
                      {p.postName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>User Type</InputLabel>
                <Select
                  value={tableLevelFilter}
                  label="User Type"
                  onChange={(e) => setTableLevelFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="2">{LEVEL_NAMES[2]}</MenuItem>
                  <MenuItem value="3">{LEVEL_NAMES[3]}</MenuItem>
                  <MenuItem value="4">{LEVEL_NAMES[4]}</MenuItem>
                  <MenuItem value="5">{LEVEL_NAMES[5]}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Loader />
        ) : (
          <Box sx={{ height: 550, width: '100%', overflow: 'auto' }}>
            <DataGrid
              rows={displayedUsers}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
              getRowHeight={() => 'auto'}
              sx={{
                border: 'none',
                width: '100%',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f8fafc',
                  fontWeight: 700,
                  color: '#1e293b',
                  fontSize: '0.85rem',
                  px: 1,
                },
                '& .MuiDataGrid-cell': {
                  py: 1.5,
                  px: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f8fafc',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #e2e8f0',
                  px: 2,
                },
              }}
            />
          </Box>
        )}
      </Paper>

        {/* Dialog for Assigning Designation */}
        <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm" scroll="paper">
          <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>Assign Designation</DialogTitle>
          <DialogContent dividers sx={{ maxHeight: '75vh', overflowY: 'auto' }}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>User Type</InputLabel>
                  <Select
                    value={dialogLevelFilter}
                    label="User Type"
                    onChange={(e) => {
                      setDialogLevelFilter(e.target.value);
                      setSelectedUserObj(null);
                      setSelectedPost('');
                    }}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="2">{LEVEL_NAMES[2]}</MenuItem>
                    <MenuItem value="3">{LEVEL_NAMES[3]}</MenuItem>
                    <MenuItem value="4">{LEVEL_NAMES[4]}</MenuItem>
                    <MenuItem value="5">{LEVEL_NAMES[5]}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={filteredUsersForDialog}
                  getOptionLabel={(option) => {
                    const lvl = levelNames[option.level] || option.levelName || `Level ${option.level}`;
                    const idVal = getDisplayId(option);
                    return `${option.firstName} ${option.lastName} (ID: ${idVal} | ${lvl} | Mobile: ${option.mobile} | Email: ${option.email})`;
                  }}
                  value={selectedUserObj}
                  onChange={handleUserSelect}
                  filterOptions={(options, state) => {
                    const input = state.inputValue.toLowerCase().trim();
                    if (!input) return options;
                    return options.filter((opt) => {
                      const idVal = getDisplayId(opt).toLowerCase();
                      const fname = (opt.firstName || '').toLowerCase();
                      const lname = (opt.lastName || '').toLowerCase();
                      const full = (opt.fullName || '').toLowerCase();
                      const mobile = (opt.mobile || '').toLowerCase();
                      const email = (opt.email || '').toLowerCase();
                      const level = (opt.levelName || '').toLowerCase();
                      return fname.includes(input) || lname.includes(input) || full.includes(input) || idVal.includes(input) || mobile.includes(input) || email.includes(input) || level.includes(input);
                    });
                  }}
                  renderOption={(props, option) => {
                    const lvl = levelNames[option.level] || option.levelName || `Level ${option.level}`;
                    const idVal = getDisplayId(option);
                    return (
                      <Box component="li" {...props} key={option.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', py: 1, borderBottom: '1px solid #f1f5f9' }}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                            {option.firstName} {option.lastName} <Typography component="span" variant="caption" sx={{ color: '#64748b', ml: 1 }}>ID: {idVal}</Typography>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Mobile: {option.mobile || 'N/A'} | Email: {option.email || 'N/A'}
                          </Typography>
                        </Box>
                        <Chip label={lvl} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User (Search Name, ID, Email, Mobile)"
                      variant="outlined"
                      placeholder="Search user by Name, ID, Email or Mobile..."
                    />
                  )}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700, color: '#0f172a' }}>
                  Select Designation:
                </Typography>
                <Paper sx={{ p: 2, maxHeight: 240, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                  <RadioGroup value={selectedPost} onChange={handlePostChange}>
                    {posts.map((post) => (
                      <FormControlLabel
                        key={post._id}
                        value={post._id}
                        control={<Radio />}
                        label={post.postName}
                      />
                    ))}
                  </RadioGroup>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} color="secondary" variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!selectedUserObj || !selectedPost}
              sx={{ fontWeight: 700 }}
            >
              Save Designation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Modal for De-assigning Designation */}
        <Dialog
          open={confirmDeassignModalOpen}
          onClose={() => setConfirmDeassignModalOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
            Confirm Designation Removal
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText sx={{ color: '#334155', fontWeight: 500 }}>
              {targetUserForDeassign && (
                <>
                  Are you sure you want to de-assign designation{' '}
                  <strong>{targetUserForDeassign.currentPost}</strong> from user{' '}
                  <strong>
                    {targetUserForDeassign.firstName} {targetUserForDeassign.lastName}
                  </strong>
                  ?
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmDeassignModalOpen(false)} color="inherit" variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleConfirmDeassign} color="error" variant="contained" sx={{ fontWeight: 700 }}>
              Confirm De-assign
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
    </Box>
  );
};

export default AssignPrivileges;